import {DataGridPro, GridFilterModel, GridSortModel, useGridApiRef} from "@mui/x-data-grid-pro";
import {ChangeEvent, useEffect, useRef, useState} from 'react';
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
    GetUsersList,
    onSetCurrentPage,
    onSetGridRows,
    RemoveUser,
    selectCurrentPage,
    selectDefaultUsersGridRows,
    selectIsUsersLoading,
    selectUsersGridRows,
    UpdateUserStatus,
} from "../../store/usersReducer";
import {DatePicker, Input, Popconfirm, Spin, Switch} from 'antd';
import GridPagination from "../GridPagination/GridPagination";
import Header from "../Header/Header";
import s from './Users.module.scss'
import {deDELocaleText} from '../../utils/dataGridLocale.de';
import {LoadingOutlined} from "@ant-design/icons";
import { normalizeGerman, stemGermanWord } from "../../helpers/funcHelper";
import { UserType } from "../../types/usersTypes";
import dayjs from "dayjs";

const {RangePicker} = DatePicker;

const Users = () => {
    const apiRef = useGridApiRef()
    const dispatch = useAppDispatch()
    const gridRows = useAppSelector(selectUsersGridRows)
    const defaultGridRows = useAppSelector(selectDefaultUsersGridRows)
    const page = useAppSelector(selectCurrentPage)

    const [selectedStatus, setSelectedStatus] = useState<'All' | 'Active' | 'Inactive'>('All')
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedDates, setSelectedDates] = useState<[string, string]>(['', ''])
    const [filterModel, setFilterModel] = useState<GridFilterModel>({items: []});
    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const isDataLoading = useAppSelector(selectIsUsersLoading)

    useEffect(() => {
        dispatch(GetUsersList())
    }, [dispatch])

    const onRemoveUser = (id: number) => {
        dispatch(RemoveUser(id))
    }

    const handleConfirm = (id: number) => {
        onRemoveUser(id);
    };

    const handleActiveStatusChange = (user: UserType, checked: boolean) => {
        dispatch(UpdateUserStatus({
            id: user.id,
            isActive: !!checked
        }))
    };

    const columns = [
        {
            field: 'first_name',
            headerName: 'Name',
            flex: 2,
            minWidth: 200,
            valueGetter: (_: any, user: UserType) => {
                const firstName = user?.first_name ?? '';
                const lastName = user?.last_name ?? '';
                return `${firstName} ${lastName}`;
            },
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 1,
            minWidth: 130,
        },
        {
            field: 'date_created',
            headerName: 'Datum',
            flex: 1,
            color: '#667085',
            minWidth: 120,
            renderCell: (params: {row: UserType}) => (
                <div style={{color: '#667085'}}>
                    {params.row?.date_created
                        ?  dayjs(params.row?.date_created).format('MMM D, YYYY')
                        : '-'
                    }
                </div>
            )
        },
        {
            field: 'is_active',
            headerName: 'Status',
            flex: 0.5,
            minWidth: 120,
            valueGetter: (params: {row: UserType}) => {
                return (
                    params.row?.is_active ? 'Aktiv' : 'Inaktiv'
                )
            },
            renderCell: (params: any) => (
                <Switch
                    checkedChildren="Aktiv"
                    unCheckedChildren="Inaktiv"
                    value={params.row.is_active}
                    onChange={(checked: boolean) => handleActiveStatusChange(params.row, checked)}
                />
            )
        },
        {
            field: '_',
            headerName: '',
            flex: 0.3,
            minWidth: 60,
            sortable: false,
            filterable: false,
            pinned: 'right',
            renderCell: (data: any) => (
                <div style={{
                    marginTop: '4px'
                }}>
                    <Popconfirm
                        title="Diese Anfrage löschen?"
                        description="Diese Aktion kann nicht rückgängig gemacht werden."
                        onConfirm={() => handleConfirm(data.row.id)}
                        okText="Ja"
                        cancelText="Nein"
                        okType="danger"
                    >
                        <button className={s.actionBtn}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"
                                 fill="none">
                                <path
                                    d="M2.5 4.99996H4.16667M4.16667 4.99996H17.5M4.16667 4.99996V16.6666C4.16667 17.1087 4.34226 17.5326 4.65482 17.8451C4.96738 18.1577 5.39131 18.3333 5.83333 18.3333H14.1667C14.6087 18.3333 15.0326 18.1577 15.3452 17.8451C15.6577 17.5326 15.8333 17.1087 15.8333 16.6666V4.99996H4.16667ZM6.66667 4.99996V3.33329C6.66667 2.89127 6.84226 2.46734 7.15482 2.15478C7.46738 1.84222 7.89131 1.66663 8.33333 1.66663H11.6667C12.1087 1.66663 12.5326 1.84222 12.8452 2.15478C13.1577 2.46734 13.3333 2.89127 13.3333 3.33329V4.99996M8.33333 9.16663V14.1666M11.6667 9.16663V14.1666"
                                    stroke="#667085" strokeWidth="1.66667" strokeLinecap="round"
                                    strokeLinejoin="round"/>
                            </svg>
                        </button>
                    </Popconfirm>
                </div>
            )
        }
    ]

    const onChangeGridPage = (page: number) => {
        dispatch(onSetCurrentPage(page))
    }

    // useEffect(() => {
    //     dispatch(onSetGridRows(defaultGridRows))
    // }, [defaultGridRows, dispatch])

    const filterByStatus = (users: UserType[], status: 'All' | 'Active' | 'Inactive'): UserType[] => {
        if (status === 'All') {
            return users
        } else if (status === 'Active') {
            return users.filter((user) => user.is_active)
        } else if (status === 'Inactive') {
            return users.filter((user) => !user.is_active)
        } else {
            return []
        }
    }

    const filterBySearch = (users: UserType[], search: string) => {
        if (!search?.length) return users;
        const matchesSearch = (r: typeof defaultGridRows[number]) => {
            const stemmedValue = stemGermanWord(normalizeGerman(search.toLowerCase()));

            const fields = [
                r.first_name,
                r.last_name,
                r.email,
            ];

            return fields.some(field => {
                const normalized = normalizeGerman(field?.toLowerCase() || '');
                return normalized.includes(stemmedValue);
            });
        };
        return users.filter(matchesSearch);
    }

    const filterByDates = (users: UserType[], dates: [string, string]) => {
        if (!dates[0]?.length || !dates[1]?.length) return users;
        const [fromDateStr, toDateStr] = dates;
        const fromDate = dayjs(fromDateStr).startOf('day');
        const toDate = dayjs(toDateStr).endOf('day');
        return users.filter((r) => {
            const rowDate = dayjs(r.date_created);
            const isWithinRange = rowDate.isValid() &&
                rowDate.isAfter(fromDate) &&
                rowDate.isBefore(toDate);
            return isWithinRange;
        })
    }

    const getCurrentGridRows = (status: 'All' | 'Active' | 'Inactive', search: string, dates: [string, string]) => {
        let rows = defaultGridRows
        let filteredRows = filterByStatus(rows, status)
        filteredRows = filterBySearch(filteredRows, search)
        filteredRows = filterByDates(filteredRows, dates)
        return filteredRows
    }

    const onStatusChange = (status: 'All' | 'Active' | 'Inactive') => {
        setSelectedStatus(status)
        onChangeGridPage(0)
        dispatch(onSetGridRows(getCurrentGridRows(status, searchTerm, selectedDates)))
    }

    const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchTerm(value);
        onChangeGridPage(0)
        dispatch(onSetGridRows(getCurrentGridRows(selectedStatus, value, selectedDates)))
    };

    const onDateChange = (_dates: any, dateStrings: [string, string]) => {
        onChangeGridPage(0)
        setSelectedDates(dateStrings)
        dispatch(onSetGridRows(getCurrentGridRows(selectedStatus, searchTerm, dateStrings)))
    };

    const didRestore = useRef(false);

    useEffect(() => {
        if (didRestore.current) return;

        const saved = sessionStorage.getItem("usersGridState");
        if (saved) {
            const {filterModel, sortModel} = JSON.parse(saved);
            setFilterModel(filterModel);
            setSortModel(sortModel);
        }

        didRestore.current = true;
    }, []);

    useEffect(() => {
        if (!didRestore.current) return; // skip first run
        sessionStorage.setItem(
            "usersGridState",
            JSON.stringify({filterModel, sortModel})
        );
    }, [filterModel, sortModel]);

    return (
        <div>
            <Header/>
            <div className={s.inner}>
                <h1 className={s.inner__title}>Liste der Benutzer</h1>
                <p className={s.inner__subtitle}>Alle Benutzer ansehen</p>
                <div className={s.modeButtons}>
                    <button
                        onClick={() => onStatusChange('All')}
                        className={selectedStatus === 'All' ? `${s.modeButton} ${s.modeButton_active}` : s.modeButton}
                    >
                        Alle
                    </button>
                    <button
                        onClick={() => onStatusChange('Active')}
                        className={selectedStatus === 'Active' ? `${s.modeButton} ${s.modeButton_active}` : s.modeButton}
                    >
                        Aktiv
                    </button>
                    <button
                        onClick={() => onStatusChange('Inactive')}
                        className={selectedStatus === 'Inactive' ? `${s.modeButton} ${s.modeButton_active}` : s.modeButton}
                    >
                        Inaktiv
                    </button>
                </div>
                <div className={s.searchAndFilters}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <Input
                            type="text"
                            value={searchTerm}
                            placeholder={'Suchen'}
                            onChange={handleSearchInputChange}
                            className={s.searchAndFilters__searchInput}
                            prefix={
                                <svg style={{
                                    marginRight: '8px'
                                }} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"
                                     fill="none">
                                    <path
                                        d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z"
                                        stroke="#667085" strokeWidth="1.66667" strokeLinecap="round"
                                        strokeLinejoin="round"/>
                                </svg>
                            }
                            allowClear
                        />
                    </div>
                    <RangePicker onChange={onDateChange} placeholder={['Startdatum', 'Enddatum']}/>
                </div>
                {
                    !isDataLoading
                    ?
                        <DataGridPro
                            localeText={deDELocaleText}
                            sx={{
                                borderRadius: '8px',
                                border: '1px solid var(--Gray-200, #EAECF0)',
                                background: '#FFF)',
                                boxShadow: '0px 1px 3px 0px rgba(16, 24, 40, 0.10), 0px 1px 2px 0px rgba(16, 24, 40, 0.06)',
                                "& .MuiDataGrid-cellContent": {
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    color: '#101828',
                                },
                                "& .MuiDataGrid-columnHeaderTitle": {
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 500,
                                    color: '#667085',
                                    fontSize: '12px',
                                    lineHeight: '18px',
                                },
                                "& .MuiDataGrid-cell:focus": {
                                    outline: 'none'
                                },

                            }}
                            apiRef={apiRef}
                            getRowId={e => e.id}
                            rows={gridRows}
                            {...gridRows}
                            hideFooterSelectedRowCount
                            sortingOrder={['desc', 'asc']}
                            rowHeight={72}
                            columnHeaderHeight={44}
                            columns={columns}
                            pagination
                            filterModel={filterModel}
                            onFilterModelChange={(newModel) => {
                                onChangeGridPage(0);
                                setFilterModel(newModel);
                            }}
                            sortModel={sortModel}
                            onSortModelChange={(newModel) => {
                                onChangeGridPage(0);
                                setSortModel(newModel);
                            }}
                            slots={{pagination: GridPagination}}
                            paginationModel={{pageSize: 10, page}}
                            onPaginationModelChange={(model) => {
                                onChangeGridPage(model.page);
                            }}
                            pageSizeOptions={[10, 25, 50]}
                            initialState={{
                                pinnedColumns: {
                                    right: ['_'],
                                },
                                sorting: {
                                    sortModel: [{field: 'date_created', sort: 'desc'}],
                                },
                            }}
                        />
                        :
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '65vh',
                            flexDirection: 'column'
                        }}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1F553B' }} spin />} />
                        </div>
                }
            </div>
        </div>
    );
};

export default Users;
